import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

export interface SavedQuiz {
  id: string;
  teacherId: string;
  name: string;
  subject: string;
  gradeLevel: string;
  examBoard?: string;
  topic: string;
  tags: string[];
  folder?: string;
  difficulty: 'Easy' | 'Medium' | 'Hard' | 'Mixed';
  questionTypes: string[];
  cognitiveLevel: string[];
  questions: any[]; // The generated questions
  totalQuestions: number;
  estimatedTime: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastUsed?: Timestamp;
  timesUsed: number;
  isTemplate: boolean;
  description?: string;
  status: 'draft' | 'template' | 'archived';
}

export interface SavedQuizInput {
  name: string;
  subject: string;
  gradeLevel: string;
  examBoard?: string;
  topic: string;
  tags: string[];
  folder?: string;
  difficulty: 'Easy' | 'Medium' | 'Hard' | 'Mixed';
  questionTypes: string[];
  cognitiveLevel: string[];
  questions: any[];
  estimatedTime: number;
  description?: string;
  isTemplate?: boolean;
}

/**
 * Estimate document size in bytes (rough approximation)
 */
function estimateDocumentSize(data: any): number {
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch {
    return 0;
  }
}

/**
 * Save a quiz to the library
 * ✅ SECURE: Uses Cloud Function to enforce plan limits server-side
 */
export async function saveQuizToLibrary(
  teacherId: string,
  quizData: SavedQuizInput
): Promise<string> {
  try {
    const quiz = {
      title: quizData.name, // Cloud Function expects 'title'
      ...quizData,
      totalQuestions: quizData.questions.length,
      timesUsed: 0,
      status: quizData.isTemplate ? 'template' : 'draft',
      isTemplate: quizData.isTemplate || false
    };

    // ✅ Validate payload size (Firestore limit is 1MB)
    const estimatedSize = estimateDocumentSize(quiz);
    const MAX_SIZE = 900000; // 900KB (leave buffer for Firestore metadata)

    if (estimatedSize > MAX_SIZE) {
      const sizeMB = (estimatedSize / 1000000).toFixed(2);
      throw new Error(
        `Quiz is too large to save (${sizeMB}MB). ` +
        `Try reducing the number of questions (currently ${quizData.questions.length}) ` +
        `or simplifying question content.`
      );
    }

    // ✅ Call Cloud Function (enforces limits + increments counter atomically)
    const saveQuizCallable = httpsCallable(functions, 'saveQuizToLibrary');
    const result = await saveQuizCallable({
      quiz,
      metadata: {
        source: 'web_app',
        version: '1.0'
      }
    });

    const data = result.data as any;

    if (!data || !data.quizId) {
      throw new Error("Cloud Function returned invalid response");
    }

    console.log(`✅ Quiz saved: ${data.savedCount}/${data.limit} (plan: ${data.plan})`);

    return data.quizId;

  } catch (error: any) {
    console.error('[saveQuizToLibrary] error:', error);

    // ✅ Handle Cloud Function errors
    if (error?.code === 'functions/resource-exhausted') {
      // Trial limit reached
      throw new Error(error.message || 'You have reached your quiz save limit. Upgrade to save more!');
    }

    if (error?.code === 'functions/permission-denied') {
      // Trial expired or no access
      throw new Error(error.message || 'Your trial has expired. Upgrade to save quizzes!');
    }

    // Better error messages for common issues
    if (error instanceof Error) {
      if (error.message.includes('too large')) {
        throw error; // Already has good message
      }
      throw error;
    }

    throw new Error('Failed to save quiz to library');
  }
}

/**
 * Get all saved quizzes for a teacher
 */
export async function getTeacherQuizzes(
  teacherId: string,
  filters: {
    subject?: string;
    status?: string;
    folder?: string;
    limit?: number;
  } = {}
): Promise<SavedQuiz[]> {
  try {
    let q = query(
      collection(db, 'quizLibrary'),
      where('teacherId', '==', teacherId),
      orderBy('updatedAt', 'desc')
    );

    if (filters.subject) {
      q = query(q, where('subject', '==', filters.subject));
    }

    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    if (filters.folder) {
      q = query(q, where('folder', '==', filters.folder));
    }

    if (filters.limit) {
      q = query(q, firestoreLimit(filters.limit));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as SavedQuiz));
  } catch (error) {
    console.error('Error getting teacher quizzes:', error);
    throw new Error('Failed to load quizzes');
  }
}

/**
 * Get a specific saved quiz
 */
export async function getSavedQuiz(quizId: string): Promise<SavedQuiz | null> {
  try {
    const docRef = doc(db, 'quizLibrary', quizId);
    const snapshot = await getDoc(docRef);

    if (snapshot.exists()) {
      return {
        id: snapshot.id,
        ...snapshot.data()
      } as SavedQuiz;
    }

    return null;
  } catch (error) {
    console.error('Error getting saved quiz:', error);
    throw new Error('Failed to load quiz');
  }
}

/**
 * Update a saved quiz
 */
export async function updateSavedQuiz(
  quizId: string,
  updates: Partial<SavedQuizInput>
): Promise<void> {
  try {
    const docRef = doc(db, 'quizLibrary', quizId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating saved quiz:', error);
    throw new Error('Failed to update quiz');
  }
}

/**
 * Delete a saved quiz
 */
export async function deleteSavedQuiz(quizId: string): Promise<void> {
  try {
    const docRef = doc(db, 'quizLibrary', quizId);
    await deleteDoc(docRef);
  } catch (error: any) {
    console.error('Error deleting saved quiz:', error);
    const errorMessage = error?.message || error?.toString() || 'Failed to delete quiz';
    throw new Error(errorMessage);
  }
}

/**
 * Duplicate a quiz (create copy)
 */
export async function duplicateQuiz(
  originalQuizId: string,
  teacherId: string,
  newName?: string
): Promise<string> {
  try {
    const originalQuiz = await getSavedQuiz(originalQuizId);
    if (!originalQuiz) {
      throw new Error('Original quiz not found');
    }

    const duplicateData: SavedQuizInput = {
      name: newName || `${originalQuiz.name} (Copy)`,
      subject: originalQuiz.subject,
      gradeLevel: originalQuiz.gradeLevel,
      examBoard: originalQuiz.examBoard,
      topic: originalQuiz.topic,
      tags: [...originalQuiz.tags],
      folder: originalQuiz.folder,
      difficulty: originalQuiz.difficulty,
      questionTypes: [...originalQuiz.questionTypes],
      cognitiveLevel: [...originalQuiz.cognitiveLevel],
      questions: [...originalQuiz.questions],
      estimatedTime: originalQuiz.estimatedTime,
      description: originalQuiz.description,
      isTemplate: false // Copies are always drafts initially
    };

    return await saveQuizToLibrary(teacherId, duplicateData);
  } catch (error) {
    console.error('Error duplicating quiz:', error);
    throw new Error('Failed to duplicate quiz');
  }
}

/**
 * Mark quiz as used (increment usage count)
 */
export async function markQuizAsUsed(quizId: string): Promise<void> {
  try {
    const docRef = doc(db, 'quizLibrary', quizId);
    const quiz = await getSavedQuiz(quizId);

    if (quiz) {
      await updateDoc(docRef, {
        timesUsed: (quiz.timesUsed || 0) + 1,
        lastUsed: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error marking quiz as used:', error);
  }
}

/**
 * Get unique folders for a teacher
 */
export async function getTeacherFolders(teacherId: string): Promise<string[]> {
  try {
    const q = query(
      collection(db, 'quizLibrary'),
      where('teacherId', '==', teacherId)
    );

    const snapshot = await getDocs(q);
    const folders = new Set<string>();

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.folder) {
        folders.add(data.folder);
      }
    });

    return Array.from(folders).sort();
  } catch (error) {
    console.error('Error getting teacher folders:', error);
    return [];
  }
}

/**
 * Search quizzes by name or tags
 */
export async function searchQuizzes(
  teacherId: string,
  searchTerm: string
): Promise<SavedQuiz[]> {
  try {
    // Get all quizzes for teacher and filter locally
    // (Firestore doesn't support full-text search without additional setup)
    const allQuizzes = await getTeacherQuizzes(teacherId);

    const searchLower = searchTerm.toLowerCase();

    return allQuizzes.filter(quiz =>
      quiz.name.toLowerCase().includes(searchLower) ||
      quiz.topic.toLowerCase().includes(searchLower) ||
      quiz.tags.some(tag => tag.toLowerCase().includes(searchLower)) ||
      quiz.description?.toLowerCase().includes(searchLower)
    );
  } catch (error) {
    console.error('Error searching quizzes:', error);
    throw new Error('Failed to search quizzes');
  }
}

/**
 * ============================================================================
 * COMMUNITY LIBRARY FUNCTIONS (Ethical Opt-In)
 * ============================================================================
 */

/**
 * Share a quiz to the community library (requires explicit opt-in)
 */
export async function shareQuizToCommunity(quizId: string): Promise<void> {
  try {
    const shareFunction = httpsCallable(functions, 'shareQuizToCommunity');
    await shareFunction({
      quizId,
      shareToCommunityLibrary: true // Explicit consent required
    });
  } catch (error: any) {
    console.error('Error sharing quiz to community:', error);
    throw new Error(error.message || 'Failed to share quiz to community library');
  }
}

/**
 * Search community library (admin or public access)
 */
export async function searchCommunityLibrary(filters: {
  searchTerm?: string;
  subject?: string;
  gradeLevel?: string;
  difficulty?: string;
  quality?: string;
  minQuestions?: number;
  sortBy?: 'newest' | 'quality' | 'mostQuestions';
  limit?: number;
}): Promise<any[]> {
  try {
    const searchFunction = httpsCallable(functions, 'searchCommunityLibrary');
    const result = await searchFunction(filters);
    return (result.data as any)?.quizzes || [];
  } catch (error: any) {
    console.error('Error searching community library:', error);
    throw new Error(error.message || 'Failed to search community library');
  }
}

/**
 * Mark quiz quality (master admin only)
 */
export async function markCommunityQuizQuality(
  quizId: string,
  quality: 'pending' | 'ok' | 'high' | 'flagged',
  notes?: string
): Promise<void> {
  try {
    const markFunction = httpsCallable(functions, 'markCommunityQuizQuality');
    await markFunction({
      quizId,
      quality,
      notes
    });
  } catch (error: any) {
    console.error('Error marking quiz quality:', error);
    throw new Error(error.message || 'Failed to update quiz quality');
  }
}

/**
 * Get community library stats (admin only)
 */
export async function getCommunityLibraryStats(): Promise<any> {
  try {
    const statsFunction = httpsCallable(functions, 'getCommunityLibraryStats');
    const result = await statsFunction();
    return (result.data as any) || {};
  } catch (error: any) {
    console.error('Error getting community library stats:', error);
    throw new Error(error.message || 'Failed to get community library stats');
  }
}